import { uuid4 } from './uuid'

export const STORAGE_KEY = 'renew:consent-queue:v1'

// A decision the journal refused and that nothing can recover. Kept rather than deleted: a
// console line is gone the moment the tab closes, and this is the only remaining trace that
// somebody pressed a button whose answer was never recorded.
export const DEAD_LETTER_KEY = 'renew:consent-dead:v1'

const MAX_DEAD_LETTERS = 20

const MAX_ATTEMPTS = 12
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 5 * 60 * 1000

// 409 is deliberately absent. It does not mean "already recorded" - it means this key was
// already spent on a *different* decision, and the journal wrote nothing. Dropping it here is
// how a consent disappears while the interface reports success. "Already recorded" is a 200
// with the same consent_id, which succeeds through the normal path.
const TERMINAL_STATUSES = [400, 401, 403, 404, 422]

const KEY_CONFLICT_STATUS = 409

// The version fields the backend validates once it has published the matching text. Note the
// spelling: this client sits outside the $http interceptors, so the keys arrive in snake_case -
// the exact opposite of the checkout, where camelcase-object-deep has already renamed them.
const VERSION_FIELDS = ['banner_version', 'cookie_list_version']

let isFlushing = false
let flushTimer = null

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeQueue(items) {
  try {
    if (!items.length) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch (e) {
    return
  }
}

function backoffDelay(attempts) {
  return Math.min(BASE_DELAY_MS * Math.pow(2, attempts), MAX_DELAY_MS)
}

function statusOf(error) {
  return error && error.response && error.response.status
}

function isTerminal(error) {
  return TERMINAL_STATUSES.indexOf(statusOf(error)) !== -1
}

function isKeyConflict(error) {
  return statusOf(error) === KEY_CONFLICT_STATUS
}

// A 422 naming one of the version fields means the text revision we sent is not one the backend
// knows. Nothing can be retried: resending under a corrected version would journal agreement to
// a wording the visitor never saw, which is worse than losing the row - the journal exists to be
// evidence. So it is separated from the other terminal failures only to be recorded loudly.
function versionRejection(error) {
  if (statusOf(error) !== 422) return null

  const errors = (error && error.response && error.response.data && error.response.data.errors) || {}
  return VERSION_FIELDS.filter((field) => errors[field])
}

function readDeadLetters() {
  try {
    const raw = localStorage.getItem(DEAD_LETTER_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function recordDeadLetter(item, reason) {
  try {
    const items = readDeadLetters()
    items.push({ key: item.key, body: item.body, reason, recordedAt: Date.now() })
    localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify(items.slice(-MAX_DEAD_LETTERS)))
  } catch (e) {
    return
  }
}

export function deadLetterLength() {
  return readDeadLetters().length
}

export function enqueue(decision) {
  const items = readQueue()

  items.push({
    key: decision.key,
    body: decision.body,
    attempts: 0,
    nextAttemptAt: Date.now(),
    createdAt: Date.now(),
  })

  writeQueue(items)
}

export function queueLength() {
  return readQueue().length
}

export async function flush(sender, onSent) {
  if (isFlushing) return
  isFlushing = true

  try {
    let items = readQueue()

    for (const item of items.slice()) {
      if (item.nextAttemptAt > Date.now()) continue

      try {
        const response = await sender(item)

        items = readQueue().filter((row) => row.key !== item.key)
        writeQueue(items)

        if (onSent) onSent(response, item)
      } catch (error) {
        items = readQueue()
        const row = items.find((entry) => entry.key === item.key)
        if (!row) continue

        // Giving up on a decision means the journal will never hold it, while the visitor
        // was told it was saved - the same silent hole the 409 handling above exists to
        // prevent. Nothing here can recover it, so at minimum it has to be visible: a 422
        // means the body itself is unacceptable and every future decision will fail the
        // same way until the cause is fixed.
        if (isTerminal(error) || row.attempts + 1 >= MAX_ATTEMPTS) {
          const rejectedVersions = versionRejection(error)

          const context = {
            key: item.key,
            action: item.body && item.body.action,
            status: statusOf(error) || 'no response',
            attempts: row.attempts + 1,
          }

          if (rejectedVersions && rejectedVersions.length) {
            console.error('consent: the journal refused the text version this decision was made under', {
              ...context,
              rejected: rejectedVersions,
              bannerVersion: item.body && item.body.banner_version,
              cookieListVersion: item.body && item.body.cookie_list_version,
            })
            recordDeadLetter(item, 'unknown_text_version')
          } else {
            console.warn('consent: dropping a decision that will never reach the journal', context)
          }

          writeQueue(items.filter((entry) => entry.key !== item.key))
          continue
        }

        // The key was spent on another decision, so this body never reached the journal.
        // Resend it under a fresh key. In normal operation the key is generated once per
        // decision and never changes, so getting here at all means a bug worth seeing.
        if (isKeyConflict(error)) {
          console.warn('consent: idempotency key reused, resending under a new one', item.key)
          row.key = uuid4()
        }

        row.attempts += 1
        row.nextAttemptAt = Date.now() + backoffDelay(row.attempts)
        writeQueue(items)
      }
    }
  } finally {
    isFlushing = false
  }

  scheduleNextFlush(sender, onSent)
}

function scheduleNextFlush(sender, onSent) {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  const items = readQueue()
  if (!items.length) return

  const earliest = items.reduce((min, item) => Math.min(min, item.nextAttemptAt), Infinity)
  const delay = Math.max(earliest - Date.now(), BASE_DELAY_MS)

  flushTimer = setTimeout(() => flush(sender, onSent), delay)
}
